import React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatRupiah } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Send, Check, X, Search, ChevronLeft, ChevronRight } from "lucide-react";

const STATUSES = ["pending", "paid", "processing", "done", "failed", "cancelled"];
const PER_PAGE = 10;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/admin/orders", { params: filter ? { status: filter } : {} });
      setOrders(r.data);
      setPage(0);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [filter]);

  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    return o.order_no?.toLowerCase().includes(q) || o.user_name?.toLowerCase().includes(q) || o.user_phone?.includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageData = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      load(); setSelected(null);
    } catch { toast.error("Gagal update status"); }
  };

  const dispatchPanel = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/dispatch`);
      toast.success("Dispatched ke master panel");
      load();
    } catch { toast.error("Gagal dispatch"); }
  };

  return (
    <div data-testid="admin-orders">
      <div className="label-mono text-zinc-500">// ORDERS</div>
      <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-1">Manajemen Order</h1>

      <div className="mt-4 flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setFilter(""); setPage(0); }} className={`badge-status ${!filter ? "bg-black text-white" : "bg-white"}`}>SEMUA</button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => { setFilter(s); setPage(0); }} className={`badge-status ${filter === s ? "bg-[#FF6A00] text-white" : "bg-white"}`}>{s.toUpperCase()}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Cari order / nama user..." className="input-brutal pl-8 py-1.5 text-xs w-56" />
        </div>
      </div>

      <div className="mt-4 border border-black bg-white overflow-x-auto">
        <div className="p-3 border-b border-black flex flex-wrap justify-between items-center gap-2">
          <div className="label-mono text-zinc-500">{filtered.length} order{search && ` (filter: "${search}")`}</div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-zinc-500">{page * PER_PAGE + 1}–{Math.min((page + 1) * PER_PAGE, filtered.length)} dari {filtered.length}</span>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30">
              <ChevronLeft className="w-3 h-3" />
            </button>
            <span className="font-mono text-[10px]">{page + 1}/{totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30">
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-black">
            <tr>
              <th className="p-3 text-left label-mono">ORDER</th>
              <th className="p-3 text-left label-mono hidden md:table-cell">USER</th>
              <th className="p-3 text-left label-mono">STATUS</th>
              <th className="p-3 text-left label-mono hidden lg:table-cell">PAYMENT</th>
              <th className="p-3 text-right label-mono">TOTAL</th>
              <th className="p-3 text-right label-mono">AKSI</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((o) => (
              <tr key={o.id} className="border-b border-zinc-200" data-testid={`admin-order-row-${o.id}`}>
                <td className="p-3 font-mono text-xs">
                  <button onClick={() => setSelected(o)} className="font-bold underline">{o.order_no}</button>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{new Date(o.created_at).toLocaleString("en-US")}</div>
                </td>
                <td className="p-3 hidden md:table-cell">
                  <div className="font-mono text-xs">{o.user_name}</div>
                  <div className="font-mono text-[10px] text-zinc-500">{o.user_phone}</div>
                </td>
                <td className="p-3"><StatusBadge status={o.status} /></td>
                <td className="p-3 hidden lg:table-cell font-mono text-[10px]">{o.payment_method.toUpperCase()}</td>
                <td className="p-3 text-right font-mono text-xs font-bold">{formatRupiah(o.total)}</td>
                <td className="p-3 text-right space-x-1">
                  {o.status === "pending" && o.payment_method === "manual_transfer" && (
                    <button onClick={() => updateStatus(o.id, "paid")} className="p-1.5 border border-black bg-[#00C853] text-white hover:bg-black" title="Konfirmasi pembayaran" data-testid={`confirm-pay-${o.id}`}><Check className="w-3 h-3" /></button>
                  )}
                  {(o.status === "paid" || o.status === "pending") && (
                    <button onClick={() => dispatchPanel(o.id)} className="p-1.5 border border-black bg-[#FF6A00] text-white hover:bg-black" title="Dispatch master panel" data-testid={`dispatch-${o.id}`}><Send className="w-3 h-3" /></button>
                  )}
                  {o.status !== "cancelled" && o.status !== "done" && (
                    <button onClick={() => updateStatus(o.id, "cancelled")} className="p-1.5 border border-black hover:bg-[#FF3333] hover:text-white" title="Batalkan"><X className="w-3 h-3" /></button>
                  )}
                </td>
              </tr>
            ))}
            {pageData.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center font-mono text-xs text-zinc-500">// {search ? `tidak ada order "${search}"` : "no orders"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center p-4 z-50" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-black shadow-brutal-lg max-w-2xl w-full max-h-[85vh] overflow-auto">
            <div className="p-4 border-b border-black flex justify-between items-center bg-black text-white">
              <div>
                <div className="label-mono text-yellow-400">// ORDER</div>
                <div className="font-display font-black uppercase">{selected.order_no}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 border border-white hover:bg-white hover:text-black"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 font-mono text-xs">
                <div><div className="text-zinc-500">USER</div>{selected.user_name} · {selected.user_phone}</div>
                <div><div className="text-zinc-500">STATUS</div><StatusBadge status={selected.status} /></div>
                <div><div className="text-zinc-500">PAYMENT</div>{selected.payment_method}</div>
                <div><div className="text-zinc-500">TOTAL</div><b>{formatRupiah(selected.total)}</b></div>
              </div>
              {selected.shipping_address && <div className="font-mono text-xs"><div className="text-zinc-500">ALAMAT</div>{selected.shipping_address}</div>}
              {selected.notes && <div className="font-mono text-xs"><div className="text-zinc-500">NOTES</div>{selected.notes}</div>}
              <div className="border border-black">
                {selected.items.map((it, i) => (
                  <div key={i} className="p-3 border-b border-zinc-200 last:border-b-0 flex justify-between text-xs font-mono">
                    <span>{it.name} ×{it.quantity}</span>
                    <span>{formatRupiah(it.subtotal)}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => updateStatus(selected.id, s)} className={`badge-status hover:bg-black hover:text-white ${selected.status === s ? "bg-black text-white" : "bg-white"}`}>SET → {s.toUpperCase()}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}